/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold transition-all duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-[hsl(var(--icon-tint-default)/0.3)] bg-[linear-gradient(135deg,hsl(var(--icon-tint-default)/0.24),hsl(var(--icon-tint-default)/0.08)] text-[hsl(var(--icon-tint-default))] shadow-[0_6px_18px_-14px_hsl(var(--icon-tint-default)/0.45)] hover:brightness-105",
        secondary:
          "border-[hsl(var(--muted-foreground)/0.2)] bg-[linear-gradient(135deg,hsl(var(--muted)/0.65),hsl(var(--muted)/0.45)] text-[hsl(var(--muted-foreground))] shadow-[0_5px_14px_-14px_hsl(var(--muted-foreground)/0.35)]",
        destructive:
          "border-[hsl(var(--destructive)/0.35)] bg-[linear-gradient(135deg,hsl(var(--destructive)/0.22),hsl(var(--destructive)/0.08)] text-[hsl(var(--destructive-foreground))] shadow-[0_6px_18px_-14px_hsl(var(--destructive)/0.35)] hover:brightness-105",
        outline:
          "border-[hsl(var(--surface-border))] bg-[linear-gradient(135deg,hsl(var(--surface-2)),hsl(var(--color-bg-muted)/0.65)] text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type BadgeIndicatorTone = "success" | "warning" | "error" | "info" | "neutral";

const indicatorToneClasses: Record<BadgeIndicatorTone, string> = {
  success: "bg-success-500",
  warning: "bg-warning-500",
  error: "bg-error-500",
  info: "bg-info-500",
  neutral: "bg-muted-foreground/60",
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  indicator?: boolean;
  indicatorTone?: BadgeIndicatorTone;
  indicatorLabel?: string;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, indicator, indicatorTone = "neutral", indicatorLabel, children, ...props }, ref) => {
    return (
      <span ref={ref} className={cn(badgeVariants({ variant }), indicator && "gap-1.5", className)} {...props}>
        {indicator && (
          <span className={cn("h-1.5 w-1.5 rounded-full", indicatorToneClasses[indicatorTone])} aria-hidden="true" />
        )}
        {children}
        {indicator && indicatorLabel && <span className="sr-only">{indicatorLabel}</span>}
      </span>
    );
  }
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };